import React, { useState, useRef, useEffect } from "react";
import { ArrowForward, ArrowBack } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  Question,
  OnboardingResponse,
} from "../../types/onboarding";
import YearPicker from "./YearPicker";
import SingleChoice from "./SingleChoice";
import MultiChoice from "./MultiChoice";
import TextInput from "./TextInput";
import Dropdown from "./Dropdown";
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import {
  getUserData,
  getUserResponse,
} from "../../utils/firebase";
import styles from "./Onboarding.module.css";
import { ensureUserId, getUserId, checkIfLoggedIn, setUserId as setUserIdToStorage } from "../../utils/userId";

interface OnboardingProps {
  questions: Question[];
}

const Onboarding: React.FC<OnboardingProps> = ({ questions }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { hospitalId } = useParams();
  const prefix = hospitalId ? `/${hospitalId}` : '';

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState<OnboardingResponse[]>([]);
  const questionRefs = useRef<(HTMLDivElement | null)[]>([]);

  const [isLoading, setIsLoading] = useState(true);
  const [onboardingUserId, setOnboardingUserId] = useState<string | null>(null);

  const currentResponse = responses.find(
    (r) => r.questionId === questions[currentQuestionIndex]?.id
  );

  useEffect(() => {
    const initializeUser = async () => {
      try {
        // 로그인 상태가 아닐 때만 온보딩 진입 시 세션 초기화
        if (!checkIfLoggedIn()) {
          sessionStorage.clear(); // 이전 세션 기록 완전 삭제
          
          // ensureUserId가 내부에서 getUserId()를 체크하므로 
          // 세션이 비워진 상태에서 호출하면 새로운 ID를 생성하게 됩니다.
          const id = await ensureUserId(); 
          setUserIdToStorage(id, false);
          setOnboardingUserId(id);
        } else {
          // 이미 로그인된 회원은 온보딩을 다시 할 필요가 없으므로 체크 페이지로 이동
          navigate(`${prefix}/check`);
        }
      } catch (error) {
        console.error("Error initializing user:", error);
      } finally {
        setIsLoading(false);
      }
    };

  initializeUser();
}, [prefix, navigate]);

  useEffect(() => {
    questionRefs.current = questionRefs.current.slice(0, questions.length);
  }, [questions.length]);

  const scrollToQuestion = (index: number) => {
    if (questionRefs.current[index]) {
      const questionElement = questionRefs.current[index];
      const rect = questionElement?.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      if (rect) {
        // 화면 정중앙에 맞추도록 스크롤 위치 계산
        const targetScrollTop =
          window.pageYOffset + rect.top - (windowHeight - rect.height) / 2;
        window.scrollTo({
          top: targetScrollTop,
          behavior: "smooth",
        });
      }
    }
  };

  useEffect(() => {
    // 현재 질문이 변경될 때마다 스크롤 조정
    if (currentQuestionIndex >= 0) {
      scrollToQuestion(currentQuestionIndex);
    }
  }, [currentQuestionIndex]);

  const handleAnswer = (answer: string | number | (string | number)[]) => {
    const newResponse: OnboardingResponse = {
      questionId: questions[currentQuestionIndex].id,
      answer,
    };

    setResponses((prev) => {
      const updatedResponses = [...prev];
      const existingIndex = prev.findIndex(
        (r) => r.questionId === newResponse.questionId
      );

      if (existingIndex >= 0) {
        updatedResponses[existingIndex] = newResponse;
      } else {
        updatedResponses.push(newResponse);
      }

      return updatedResponses;
    });
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      const nextIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(nextIndex);
    } else {
      handleComplete();
    }
  };

  const handleComplete = async () => {
    if (responses.length === 0) {
      console.error("No responses to submit");
      return;
    }

    if (responses.length !== questions.length) {
      console.error(
        `Incomplete responses: ${responses.length} of ${questions.length} questions answered`
      );
      return;
    }

    const formattedResponses = responses.reduce((acc, response) => {
      acc[response.questionId] = response.answer;
      return acc;
    }, {} as Record<string, any>);

    try {
      if (!onboardingUserId) {
        console.error("User ID is missing, cannot save");
        return;
      }

      const userDocRef = doc(db, 'users', onboardingUserId);

      await updateDoc(userDocRef, {
        responses: formattedResponses,
        language: navigator.language,
        updatedAt: new Date()
      });

      navigate(`${prefix}/check`);
      
    } catch (error) {
      console.error("Error in handleComplete (saving to Firestore):", error);
    }
  };

  const handlePreviousAnswerClick = (index: number) => {
    setCurrentQuestionIndex(index);
  };

  const renderPreviousAnswers = () => {
    const totalPrevious = currentQuestionIndex;
    return responses.slice(0, currentQuestionIndex).map((response, index) => {
      const question = questions[index];
      const age = totalPrevious - index;

      const scale = Math.max(1.0 - age * 0.1, 0.7); 
      const translateY = age * 25;
      const opacity = Math.max(1.0 - age * 0.4, 0.3);
      const zIndex = 100 - age;  
      const filter = age > 0 ? `blur(${age * 1.5}px)` : "none"; 
      return (
        <div
          key={response.questionId}
          className={styles.previousAnswerItem}
          onClick={() => handlePreviousAnswerClick(index)}
          style={{
            transform: `translateY(${translateY}px) scale(${scale})`,
            opacity: opacity,
            zIndex: zIndex,
            filter: filter,
          }}
        >
          <span className={styles.previousQuestionText}>
            {t(question.questionTextKey)}
          </span>
          <span className={styles.previousAnswerText}>
            {Array.isArray(response.answer)
              ? response.answer
                  .map((ans) => {
                    // Find the option that matches this answer
                    if (question.type === "year_picker") {
                      return ans.toString();
                    }
                    const option = question.options?.find(
                      (opt) => opt.value.toString() === ans.toString()
                    );
                    return option ? t(option.optionTextKey) : ans.toString();
                  })
                  .join(", ")
              : (() => {
                  // Find the option that matches this answer
                  if (question.type === "year_picker") {
                    return response.answer.toString();
                  }
                  const option = question.options?.find(
                    (opt) => opt.value.toString() === response.answer.toString()
                  );
                  return option
                    ? t(option.optionTextKey)
                    : response.answer.toString();
                })()}
          </span>
        </div>
      );
    });
  };

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = 1920; year <= currentYear; year++) {
      years.push({
        value: year.toString(),
        labelKey: year.toString(),
      });
    }
    return years;
  };

  // TODO(종현): 컴포넌트 분리해서 /update 페이지에도 쓰기
  const renderQuestion = (question: Question, index: number) => {
    const currentResponse = responses.find((r) => r.questionId === question.id);

    const isActive = index === currentQuestionIndex;

    return (
      <div
        key={question.id}
        ref={(el: HTMLDivElement | null) => {
          questionRefs.current[index] = el;
        }}
        className={styles.questionWrapper}
        data-active={isActive}
        data-previous={index < currentQuestionIndex}
        data-question-id={question.id}
        style={{
          display:
            index < currentQuestionIndex - 1 || index > currentQuestionIndex + 1
              ? "none"
              : "block",
        }}
      >
        <div className={styles.questionNumber}>
          {`${index + 1}/${questions.length}`}
        </div>
        <h2
          className={`${styles.questionText} ${
            question.type === "multi_choice" ? styles.multiChoiceQuestion : ""
          }`}
        >
          {t(question.questionTextKey)}
        </h2>

        <div
          className={
            question.type === "text_input" ? styles.inputContainer : ""
          }
          style={
             question.type !== "text_input" ? { width: '100%', marginBottom: '30px' } : {}
          }
        >
          {(() => {
            if (index === 0) {
              return (
                <Dropdown
                  questionTextKey={question.questionTextKey}
                  options={generateYearOptions()}
                  value={(currentResponse?.answer as string) || ""}
                  onChange={handleAnswer}
                />
              );
            }

            if (index < 3) {
              return (
                <Dropdown
                  questionTextKey={question.questionTextKey}
                  options={
                    question.options?.map((opt: any) => ({
                      value: opt.value.toString(),
                      labelKey: opt.optionTextKey,
                    })) || []
                  }
                  value={(currentResponse?.answer as string) || ""}
                  onChange={handleAnswer}
                />
              );
            }

            switch (question.type) {
              case "year_picker":
                return (
                  <YearPicker
                    questionTextKey={question.questionTextKey}
                    value={currentResponse?.answer as number}
                    onChange={handleAnswer}
                    options={question.options || []}
                  />
                );
              case "single_choice":
                return (
                  <SingleChoice
                    questionTextKey={question.questionTextKey}
                    options={question.options || []}
                    value={currentResponse?.answer as string}
                    onChange={handleAnswer}
                  />
                );
              case "multi_choice":
                return (
                  <MultiChoice
                    questionTextKey={question.questionTextKey}
                    options={question.options || []}
                    value={(currentResponse?.answer as string[]) || []}
                    onChange={handleAnswer}
                  />
                );
              case "text_input":
                return (
                  <TextInput
                    questionTextKey={question.questionTextKey}
                    value={currentResponse?.answer as string}
                    onChange={handleAnswer}
                  />
                );
              default:
                return null;
            }
          })()}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.logoContainer}>
          <span className={styles.logoText}>Brain</span>
          <span className={styles.logoText}>Check</span>
        </div>
        <button 
          className={styles.headerLoginButton} 
          onClick={() => navigate(`${prefix}/login`)}
        >
          {t('login', '로그인')}
        </button>
    </div>

      <div className={styles.previousAnswersContainer}>
        {renderPreviousAnswers()}
      </div>

      <div className={styles.questionsContainer}>
        {questions.map((question, index) => renderQuestion(question, index))}
      </div>
      <div className={styles.navigationButtonContainer}>
        {currentQuestionIndex > 0 && (
          <button
            className={styles.navigationButton}
            onClick={() => {
              const prevIndex = currentQuestionIndex - 1;
              setCurrentQuestionIndex(prevIndex);
            }}
          >
            <ArrowBack />
          </button>
        )}
        <button
          className={`${styles.navigationButton} ${
            currentQuestionIndex === 0 ? styles.firstQuestionContinue : ""
          }`}
          onClick={() => {
            if (currentResponse) {
              handleNext();
            }
          }}
          disabled={!currentResponse}
        >
          {currentQuestionIndex === questions.length - 1
            ? t("common_submit")
            : t("common_next")}
          {currentQuestionIndex !== questions.length - 1 && <ArrowForward />}
        </button>
      </div>
    </div>
  );
};

export default Onboarding;
